import { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useGeocodingSearch } from 'transfix-maps/hooks/useGeocodingSearch'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IShipment } from 'types'
import { IDriverLocation, TSourceType } from 'types/Location'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useGetShipmentLocations } from './useGetShipmentLocations'

/**
 * Parses Mapbox result into a location string
 * @returns 'New York, NY'
 */
const getLocationFromResults = (
  results: ILocationSuggestion[] = []
): ILocationSuggestion | undefined => {
  if (!results.length) return undefined
  const [locationItem] = results || []
  return locationItem
}

/**
 * Grab the first result from useGetShipmentPath()'s data
 */
const getLastPing = (
  data: { shipmentLocations: IDriverLocation[] } | null
): IDriverLocation | undefined => {
  if (!data) return undefined
  const { shipmentLocations = [] } = data ?? {}
  const [firstResult] = shipmentLocations || []
  return firstResult
}

/**
 * Return typed value with fallback from last ping
 * @example getLastPingProp(data, 'createdAt')
 */
function getLastPingProp<K extends keyof IDriverLocation>(
  data: { shipmentLocations: IDriverLocation[] } | null,
  keyName: K
): IDriverLocation[K] | undefined {
  const lastPing = getLastPing(data)
  if (!lastPing) return undefined
  return lastPing[keyName]
}

const getLastPingDate = (
  data: { shipmentLocations: IDriverLocation[] } | null
): moment.Moment | undefined => {
  const phoneTimestamp = getLastPingProp(data, 'createdAt')
  if (!phoneTimestamp) return undefined
  return moment(phoneTimestamp)
}

/**
 * Returns last ping's date, in relative time
 * @returns '2 days ago'
 */
const getLastPingDateFormatted = (
  data: { shipmentLocations: IDriverLocation[] } | null
): string | undefined => {
  const date = getLastPingDate(data)
  if (!date) return ''
  return date.fromNow()
}

/**
 * Returns source of ping
 * @returns 'React Native'
 */
const getLastPingSource = (
  data: { shipmentLocations: IDriverLocation[] } | null
): TSourceType | string | undefined => {
  return getLastPingProp(data, 'sourceType')
}

interface IUseDriverLocationProps {
  shipment: IShipment
}

/**
 * Stores state for driver's location and Mapbox query results
 * for use with <Map />
 */
export const useDriverLocation = (props: IUseDriverLocationProps) => {
  const { shipment } = props || {}
  const { id: shipmentId } = shipment || {}

  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFpCarrierPortalEnabled = false } = getFlag('fpCarrierPortal')

  const { getResults, results = [], isLoading: isGeocodingSearchLoading } = useGeocodingSearch()
  const [isLoading, setLoading] = useState<boolean>(true)
  const { data, refetch } = useGetShipmentLocations({
    shipmentId: `${shipmentId}`,
    lazy: true,
    debounce: true,
  })
  const { shipmentLocations = [] } = data ?? {}

  /**
   * Ensure that this only fetches once per mount
   */
  const hasTriggeredFetch = useRef(false)
  useEffect(() => {
    if (!isFpCarrierPortalEnabled) return
    async function fetchPathResults() {
      if (!hasTriggeredFetch.current) {
        hasTriggeredFetch.current = true
        await refetch({
          pathParams: {
            shipmentId,
          },
        })
      }
    }

    if (shipmentId) {
      fetchPathResults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId])

  /**
   * Only fetch when `data` is valid
   */
  const hasTriggeredMapboxFetch = useRef(false)
  useEffect(() => {
    if (!isFpCarrierPortalEnabled) return
    async function fetchMapboxResults() {
      hasTriggeredMapboxFetch.current = true
      const [firstResult] = data?.shipmentLocations ?? []
      const { latitude, longitude } = firstResult || {}
      if (longitude && latitude) {
        await getResults('', {
          latitude: Number(latitude),
          longitude: Number(longitude),
        })
      }
      setLoading(false)
    }
    if (!!data?.shipmentLocations && !hasTriggeredMapboxFetch.current) {
      fetchMapboxResults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const location = useMemo(() => getLocationFromResults(results), [results])
  const dateFormatted = useMemo(() => getLastPingDateFormatted(data), [data])
  const date = useMemo(() => getLastPingDate(data), [data])
  const source = useMemo(() => getLastPingSource(data), [data])
  const lastPing = useMemo(() => getLastPing(data), [data])

  return {
    isLoading,
    isGeocodingSearchLoading,
    location,
    date,
    dateFormatted,
    source,
    shipmentLocations,
    lastPing,
  }
}
